import { useState } from 'react';
import type { WelcomeEmailStatus } from '@teachpartner/contracts';
import { describeError } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';

interface Props {
  readonly code: string;
  readonly status: WelcomeEmailStatus | null;
  onChanged(): void;
}

export function notSentText(status: WelcomeEmailStatus): string {
  return `Welcome email not sent: ${status.error ?? 'unknown error'}`;
}

function StatusLine({ status }: { status: WelcomeEmailStatus | null }) {
  if (!status) return <p>Welcome email not sent yet.</p>;
  if (status.state === 'sent') return <p>Welcome email sent {status.at}.</p>;
  return <p className="banner banner-warn">{notSentText(status)}</p>;
}

/** Welcome email to the first Tenant Admin (central SMTP); shown only before enrollment. */
export function WelcomeEmailSection({ code, status, onChanged }: Props) {
  const { api } = useServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { welcomeEmail } = await api.resendWelcomeEmail(code);
      if (welcomeEmail.state === 'sent') setNotice('Welcome email sent.');
      else setError(notSentText(welcomeEmail));
      onChanged();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Welcome email">
      <h2>Welcome email</h2>
      <StatusLine status={status} />
      <ErrorBanner message={error} />
      {notice && <div className="banner banner-info">{notice}</div>}
      <button type="button" disabled={busy} onClick={() => void resend()}>
        Resend welcome email
      </button>
    </section>
  );
}
